import * as React from 'react';
import { Helmet } from 'react-helmet';
import { Fabric, PrimaryButton } from '@fluentui/react';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { useSelector } from 'react-redux';
import { authSelector } from 'store/auth/selector';
import { BetreuerFilter, useBetreuer, useBetreuerByNr, useBetreuerList } from 'api/betreuer';
import { SelectOption } from 'formComponents/Select/types';
import { useHistory, useParams } from 'react-router-dom';
import { lookupsLoadingFinishedSelector } from 'store/lookups/selector';
import useDebouncedState from 'hooks/useDebouncedState';
import { BetreuerItem } from 'components/Betreuer/BetreuerItem';
import { viewStyles } from 'styles/viewStyles';
import { ValueType } from 'react-select';
import { Select } from 'formComponents/Select/Select';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { formStyles } from 'styles/formStyles';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { RecordViewUrlParams } from 'layout/App/types';
import { BetreuerEdit, BetreuerEditData } from 'components/Betreuer/BetreuerEdit';

const styles = mergeStyleSets({
    container: {
        position: 'relative',
        height: 'calc(100vh - 75px)',
        display: 'grid',
        gridTemplateRows: 'max-content 1fr',
        gridRowGap: 10,
    },
    searchBar: {
        display: 'grid',
        gridTemplateColumns: '200px max-content 1fr max-content',
        gridColumnGap: 10,
        marginTop: 10,
    },
});

const alleBetreuerOption = { value: '__all__', label: 'Alle Betreuer' };

interface FilterValues {
    betreuer: SelectOption;
    showInactive: boolean;
}

const defaultFilterValues: FilterValues = {
    betreuer: alleBetreuerOption,
    showInactive: false,
};

export const BetreuerView = (): JSX.Element => {
    const auth = useSelector(authSelector);
    const history = useHistory();
    const lookupsLoadingFinished = useSelector(lookupsLoadingFinishedSelector);
    const betreuer = useBetreuer();
    const betreuerByNr = useBetreuerByNr();

    const { urlRecordNr: urlBetrNr } = useParams<RecordViewUrlParams>();
    const betrNr = urlBetrNr ? parseInt(urlBetrNr, 10) : undefined;

    const betreuerFilterOptions = React.useMemo<SelectOption[]>(() => {
        const options: SelectOption[] = [];

        if (auth.rolle !== 'GF') {
            options.push({
                value: auth.nr?.toString() || '',
                label: auth.name || '?',
            });
        } else {
            options.push(alleBetreuerOption);
            betreuer
                .filter((b) => b.aktiv && b.name)
                .sort((a, b) => a.name.localeCompare(b.name))
                .forEach((b) => options.push({ value: b.nr.toString(), label: b.name }));
        }

        return options;
    }, [auth, betreuer]);

    const [filterValues, debouncedFilterValues, setFilterValues] = useDebouncedState<FilterValues>(() => {
        if (auth.rolle !== 'GF') {
            return {
                ...defaultFilterValues,
                betreuer: {
                    value: auth.kz || '',
                    label: auth.name || '?',
                },
            };
        }
        return {
            ...defaultFilterValues,
            betreuer: alleBetreuerOption,
        };
    }, 1000);

    const filter = React.useMemo<BetreuerFilter>(() => {
        return {
            betrNr:
                debouncedFilterValues.betreuer.value === '__all__'
                    ? undefined
                    : parseInt(debouncedFilterValues.betreuer.value, 10),
            aktiv: debouncedFilterValues.showInactive ? undefined : true,
            withBetreuerWorkTimeList: true,
        };
    }, [debouncedFilterValues]);

    const betreuerEditData = React.useMemo<BetreuerEditData | null>(() => {
        if (betrNr) {
            return {
                betrNr,
            };
        }
        return null;
    }, [betrNr]);

    const betreuerList = useBetreuerList(filter);

    if (!lookupsLoadingFinished) {
        return <div />;
    }

    return (
        <Fabric className={styles.container}>
            <Helmet>
                <title>{betrNr ? 'Betreuer ' + betrNr + ' | ' + (betreuerByNr[betrNr]?.kz || '') : ''}</title>
            </Helmet>
            <div className={styles.searchBar}>
                <Select
                    name="betreuer"
                    value={filterValues.betreuer}
                    options={betreuerFilterOptions}
                    onChange={(newValue: ValueType<SelectOption, false>): void => {
                        setFilterValues({
                            ...filterValues,
                            betreuer: newValue || alleBetreuerOption,
                        });
                    }}
                />

                <div />

                <div>
                    <Checkbox
                        className={formStyles.checkboxInsideForm}
                        label="Inaktive Betreuer anzeigen"
                        checked={filterValues.showInactive}
                        onChange={(event, checked) => {
                            setFilterValues({
                                ...filterValues,
                                showInactive: checked || false,
                            });
                        }}
                    />
                </div>

                <PrimaryButton onClick={() => history.push('/extras/betreuer/-1')} text="Neuer Betreuer" />
            </div>
            {betreuerList.isLoading || betreuerList.isFetching ? (
                <LoadingIndicator />
            ) : (
                <div className={viewStyles.listContainerScroll}>
                    {betreuerList.data && betreuerList?.data.length === 0 ? (
                        <div className={viewStyles.noData}>Keine Betreuer.</div>
                    ) : (
                        (betreuerList.data || []).map((betreuer2) => (
                            <BetreuerItem
                                key={betreuer2.nr}
                                betreuer={betreuer2}
                                editBetreuer={() => history.push('/extras/betreuer/' + betreuer2.nr)}
                            />
                        ))
                    )}
                </div>
            )}
            {betreuerEditData !== null && (
                <BetreuerEdit
                    betrNr={betreuerEditData.betrNr}
                    onClose={(refetch) => {
                        if (refetch) {
                            betreuerList.refetch().then();
                        }
                        history.push('/extras/betreuer/', betreuerEditData.betrNr);
                    }}
                    onReopen={(newBetrNr: number) => {
                        history.push('/extras/betreuer/', newBetrNr);
                        betreuerList.refetch().then(() => {
                            history.push('/extras/betreuer/' + newBetrNr);
                        });
                    }}
                />
            )}
        </Fabric>
    );
};
